import osmImage from "../img/basemaps/osm.png";
import ortoImage from "../img/basemaps/orto2.png";
import { PlaceSubtype } from "./enums";

export interface IConfig {
  defaults: IDefaults;
  basemaps: IBasemap[];
  objects: IService;
  geoLocation?: ILocation;
  viewExtent: number[];
  images: IImages;
  search: ISearch;
}

export interface IDefaults {
  center: number[];
  zoom: number;
  clusterLayerId: string;
  tracksLayerId: string;
  zoomToLevel: number;
  debugCoordinatesOnMap?: boolean; // true, to log out coordinates on map
}
export interface IBasemap {
  title: string;
  id: string;
  img: string;
  layers: IBasemapLayer[];
}
export interface IBasemapLayer {
  type: string;
  version?: string;
  spatialReference?: number;
  layers?: string[];
  imagetype?: string;
  url?: string;
  copyright?: string;
  visible: boolean;
}
export interface IService {
  title: string;
  id: string;
  url: string;
}
export interface IServiceUrl {
  url: string;
}
export interface IFilterOptions {
  title: string;
  value: number | string | boolean | number[];
}
export interface ILocation {
  geolocationOptions?: {
    maximumAge: number;
    timeout: number;
    enableHighAccuracy: boolean;
  };
}
export interface IExtent {
  xmax: number;
  xmin: number;
  ymax: number;
  ymin: number;
  spatialReference: {
    wkid: number;
  };
}
export interface IImages {
  url: string;
  layerBrowseName: string;
  fileTableName: string;
  imageDetails: {
    height: number;
    thumbnailHeight: number;
  };
}

export interface ISearch {
  // options: any;
  // indexEng?: string[];
  fieldsLV: string[];
  fieldsENG: string[];
  searchFields?: string[];
}

export interface IFilterConfig {
  [fieldname: string]: IFilterOptions[];
}

const placeSubtypes: Record<PlaceSubtype, string> = {
  [PlaceSubtype.nr1]: "PLACESUBTYPE=1",
  [PlaceSubtype.AtputasVieta]: "Atpūtas vieta",
  [PlaceSubtype.DabasTaka]: "Dabas taka",
  [PlaceSubtype.AktivaAtputa_VeloTaka]: "Velotaka",
  [PlaceSubtype.DabasApskatesObjekts]: "Dabas apskates objekts",
  [PlaceSubtype.KulturvesturisksApskatesObjekts]:
    "Kultūrvēsturisks apskates objekts",
  [PlaceSubtype.MammadabaGalamerki]: "Mammadaba galamērķi",
  [PlaceSubtype.SkatuTornis]: "Skatu tornis",
  [PlaceSubtype.AktivaAtputa_Orientesanas]: "Orientēšanās",
  [PlaceSubtype.Udensturistiem]: "Ūdenstūristu atpūtas vieta",
  [PlaceSubtype.MarsrutiIedvesmai]: "Maršruti iedvesmai",
};

const placeSubtypesToShow: PlaceSubtype[] = [
  PlaceSubtype.nr1,
  PlaceSubtype.AtputasVieta,
  PlaceSubtype.DabasTaka,
  PlaceSubtype.AktivaAtputa_VeloTaka,
  PlaceSubtype.DabasApskatesObjekts,
  PlaceSubtype.KulturvesturisksApskatesObjekts,
  PlaceSubtype.MammadabaGalamerki,
  PlaceSubtype.SkatuTornis,
  PlaceSubtype.AktivaAtputa_Orientesanas,
  PlaceSubtype.Udensturistiem,
  PlaceSubtype.MarsrutiIedvesmai,
];

export const getFiltersConfig = (): IFilterConfig[] => {
  return [
    {
      PLACESUBTYPE: [
        ...placeSubtypesToShow.map((value) => ({
          title: placeSubtypes[value],
          value,
        })),
        { title: "Aktīvā atpūta", value: [4, 10] },
      ],
    },
    {
      VISITED: [{ title: "Emu bijis", value: 1 }],
    },
    {
      LIKE: [{ title: "Patīk", value: 1 }],
    },
    {
      LVREGION: [
        { title: "Kurzeme", value: "1" },
        { title: "Latgale", value: "2" },
        { title: "Rīga un Pierīga", value: "3" },
        { title: "Vidzeme", value: "4" },
        { title: "Zemgale", value: "5" },
      ],
    },
    { nearby: [{ title: "Tuvākie", value: false }] },
  ];
};

const proxyUrl = "https://dabaskartes.lvm.lv/beta/proxy/";

const mammaEndpoint = "20fbdec532314a6ca93906d5a1bd2a5d";
// const mammaEndpoint = "mamma1b719989bb6144a28e0d804259abb01d";
// const mammaEndpoint = "test1b3d73a1ef3e4b6e9662e23a515832bc";

const imageEndpoint = "img0db4b879e5b49a811a4ad5bbd3d42"; // uz RPFileApi
// const imageEndpoint = "test_img0db4b879e5b49a811a4ad5bbd3d42"; // uz RPFileApiBeta

export const config: IConfig = {
  defaults: {
    center: [2695217.034668637, 7728877.262046031],
    zoom: 7.0670086534273295,
    clusterLayerId: "clusterLayer",
    tracksLayerId: "tracksLayer",
    zoomToLevel: 13,
    debugCoordinatesOnMap: false, // set true, to display coordinates on map
  },
  basemaps: [
    {
      title: "Open Street Map",
      id: "osm",
      img: osmImage,
      layers: [
        {
          type: "OSM",
          copyright: "© OpenStreetMap veidotāji",
          visible: true,
        },
      ],
    },
    {
      title: "Ortofoto karte",
      id: "orto2",
      img: ortoImage,
      layers: [
        {
          type: "WMS",
          version: "1.3.0",
          spatialReference: 3857,
          layers: ["public:Orto_LKS"],
          imagetype: "image/vnd.jpeg-png8",
          url: "https://lvmgeoserver.lvm.lv/geoserver/ows",
          visible: true,
        },
      ],
    },
  ],
  objects: {
    title: "Meža dienas punkti",
    id: "objects",
    url: proxyUrl + mammaEndpoint + "/LVMvPublicEventsWGS/FeatureServer",
  },
  geoLocation: {
    geolocationOptions: {
      maximumAge: 0,
      timeout: 15000,
      enableHighAccuracy: true,
    },
  },
  viewExtent: [1900000, 7300000, 3500000, 8200000],
  images: {
    url: proxyUrl + imageEndpoint + "/api/",
    layerBrowseName: "PUBSDE.RecreationPlace",
    fileTableName: "PUBSDE.RecreationPlacePhoto",
    imageDetails: {
      height: 600,
      thumbnailHeight: 150,
    },
  },
  search: {
    fieldsLV: ["PLACENAME", "KEYWORDS", "ADRESS"], // fields for LV search
    fieldsENG: ["PLACENAMEENG", "KEYWORDS", "ADRESS"], // fields for LV search
  },
};
